<template>
  <v-dialog :value="value" max-width="700" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>{{ routine ? 'Routine bearbeiten' : 'Neue Routine' }}</v-card-title>
      <v-card-text>
        <v-text-field v-model="name" label="Name" autofocus></v-text-field>

        <template v-for="section in sections">
          <div :key="section.key + '-head'" class="text-subtitle-2 mt-3 mb-1">
            {{ section.label }}
          </div>
          <v-row
              v-for="(action, index) in section.actions"
              :key="section.key + '-' + index"
              dense
              align="center"
          >
            <v-col cols="12" sm="4">
              <v-select
                  v-model="action.device_id"
                  :items="deviceItems"
                  label="Gerät"
                  dense
                  hide-details
              ></v-select>
            </v-col>
            <v-col cols="4" sm="2">
              <v-btn-toggle
                  :value="action.on"
                  mandatory
                  dense
                  @change="action.on = $event"
              >
                <v-btn small :value="true">Ein</v-btn>
                <v-btn small :value="false">Aus</v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="4" sm="3">
              <v-select
                  v-model="action.color"
                  :items="colorItems"
                  label="Farbe"
                  dense
                  hide-details
                  :disabled="!action.on || !isColorLamp(action.device_id)"
              ></v-select>
            </v-col>
            <v-col cols="3" sm="2">
              <v-text-field
                  v-model.number="action.brightness"
                  label="Helligkeit"
                  type="number"
                  min="0"
                  max="254"
                  dense
                  hide-details
                  clearable
                  :disabled="!action.on || !isLamp(action.device_id)"
              ></v-text-field>
            </v-col>
            <v-col cols="1">
              <v-btn icon small @click="section.actions.splice(index, 1)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <div :key="section.key + '-add'" class="mb-2">
            <v-btn text small @click="addAction(section.actions)">
              <v-icon left small>mdi-plus</v-icon>
              Aktion hinzufügen
            </v-btn>
            <v-btn
                v-if="section.key === 'off' && onActions.length"
                text
                small
                @click="fillOffFromOn"
            >
              <v-icon left small>mdi-lightbulb-off-outline</v-icon>
              Alle Geräte der Einschalt-Aktionen ausschalten
            </v-btn>
          </div>
        </template>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Abbrechen</v-btn>
        <v-btn color="primary" text :disabled="!valid" @click="save">Speichern</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { COLOR_PRESETS } from '../../../services/smarthaus'

export default {
  name: 'RoutineEditDialog',
  props: {
    value: { type: Boolean, default: false },
    // null = neue Routine anlegen, sonst die zu bearbeitende Routine.
    routine: { type: Object, default: null },
    devices: { type: Array, default: () => [] },
  },
  data: () => ({
    name: '',
    onActions: [],
    offActions: [],
  }),
  computed: {
    sections() {
      return [
        { key: 'on', label: 'Beim Einschalten', actions: this.onActions },
        { key: 'off', label: 'Beim Ausschalten', actions: this.offActions },
      ]
    },
    deviceItems() {
      return this.devices
          .filter((d) => d.category === 'lamp' || d.category === 'plug')
          .map((d) => ({ text: d.display_name, value: d.id }))
    },
    colorItems() {
      return [{ text: '— keine —', value: null }].concat(
          COLOR_PRESETS.map((p) => ({ text: p.label, value: p.rgb })),
      )
    },
    valid() {
      const filled = (a) => !!a.device_id
      return this.name.trim().length > 0
          && this.onActions.every(filled)
          && this.offActions.every(filled)
    },
  },
  watch: {
    value(open) {
      if (open) this.reset()
    },
  },
  methods: {
    reset() {
      this.name = this.routine ? this.routine.name : ''
      this.onActions = this.routine ? this.routine.on_actions.map(this.toEditable) : []
      this.offActions = this.routine ? this.routine.off_actions.map(this.toEditable) : []
    },
    // API-Aktion ({color: {red,green,blue}}) → Editor-Zeile (color als [r,g,b] der Preset-Auswahl).
    toEditable(action) {
      return {
        device_id: action.device_id,
        on: action.on,
        color: action.color ? [action.color.red, action.color.green, action.color.blue] : null,
        brightness: action.brightness != null ? action.brightness : null,
      }
    },
    toApi(action) {
      const lamp = this.isLamp(action.device_id)
      return {
        device_id: action.device_id,
        on: action.on,
        color: action.on && lamp && action.color
            ? { red: action.color[0], green: action.color[1], blue: action.color[2] }
            : null,
        brightness: action.on && lamp && action.brightness !== null && action.brightness !== ''
            ? Math.max(0, Math.min(254, Math.round(action.brightness)))
            : null,
      }
    },
    device(id) {
      return this.devices.find((d) => d.id === id)
    },
    isLamp(id) {
      const d = this.device(id)
      return !!d && d.category === 'lamp'
    },
    isColorLamp(id) {
      const d = this.device(id)
      return !!d && d.category === 'lamp' && d.supports_color
    },
    addAction(list) {
      list.push({ device_id: null, on: true, color: null, brightness: null })
    },
    fillOffFromOn() {
      this.offActions = this.onActions
          .filter((a) => a.device_id)
          .map((a) => ({ device_id: a.device_id, on: false, color: null, brightness: null }))
    },
    save() {
      this.$emit('save', {
        name: this.name.trim(),
        on_actions: this.onActions.map(this.toApi),
        off_actions: this.offActions.map(this.toApi),
      })
      this.$emit('input', false)
    },
  },
}
</script>
